import React from 'react';
import { useTable, useFilters, useGlobalFilter, useSortBy } from 'react-table';
import { matchSorter } from 'match-sorter';
import { Table, Spinner } from 'react-bootstrap';
import { ReactSortable } from 'react-sortablejs';

function DefaultColumnFilter({
  column: { filterValue, preFilteredRows, setFilter },
}) {
  const count = preFilteredRows.length

  return (
    <div className="mt-3">
      <input
        className="form-control form-control-sm rounded-pill"
        value={filterValue || ''}
        onChange={e => {
          setFilter(e.target.value || undefined) // Set undefined to remove the filter entirely
        }}
        placeholder={`Buscar ${count} registros...`}
      />
    </div>
  )
}
function fuzzyTextFilterFn(rows, id, filterValue) {
  return matchSorter(rows, filterValue, { keys: [row => row.values[id]] })
}

fuzzyTextFilterFn.autoRemove = val => !val

const TableBody = ({ getTableBodyProps, rows, prepareRow, maySortableRows, handleAddFunc, handleSortFunc }) => {
  const [sortableState, setSortableState] = React.useState(rows);
  React.useEffect(() => {
    const _rows = rows.sort((a, b) => a.original.secuencia - b.original.secuencia)
    setSortableState(_rows);
  }, [rows, maySortableRows]);
  const onSort = () => {
    if (handleSortFunc){
      handleSortFunc(sortableState);
    }
  }
  return (
    maySortableRows ? (
      <ReactSortable 
        tag="tbody"
        list={sortableState}
        setList={setSortableState}
        {...getTableBodyProps()} 
        onSort={(_, sortable, evt) => onSort()}>
        {sortableState.map((row, i) => {
          prepareRow(row)
          return (
            <tr {...row.getRowProps()} onClick={() => handleAddFunc(row.original)}>
              {row.cells.map(cell => {
                return <td {...cell.getCellProps()}> {cell.render('Cell')}</td>
              })}
            </tr>
          )
        })}
      </ReactSortable>
    ) : (
      <tbody {...getTableBodyProps()}>
        {rows.map((row, i) => {
          prepareRow(row)
          return (
            <tr {...row.getRowProps()} onClick={() => handleAddFunc(row.original)}>
              {row.cells.map(cell => {
                return <td {...cell.getCellProps()}>{cell.render('Cell')}</td>
              })}
            </tr>
          )
        })}
      </tbody>
    )
  )
}

function CustomTable({ columns, data, isLoadingData, addFunc, closeSearchModal, maySortableRows, handleSortFunc, ...props }) {
  const filterTypes = React.useMemo(
    () => ({
      // Add a new fuzzyTextFilterFn filter type.
      fuzzyText: fuzzyTextFilterFn,
      // Or, override the default text filter to use
      // "startWith"
      text: (rows, id, filterValue) => {
        return rows.filter(row => {
          const rowValue = row.values[id]
          return rowValue !== undefined
            ? String(rowValue)
                .toLowerCase()
                .startsWith(String(filterValue).toLowerCase())
            : true
        })
      },
    }),
    []
  )

  const defaultColumn = React.useMemo(
    () => ({
      // Let's set up our default Filter UI
      Filter: DefaultColumnFilter,
    }),
    []
  )

  const {
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    /*state,
    visibleColumns,
    preGlobalFilteredRows,
    setGlobalFilter,*/
  } = useTable(
    {
      columns,
      data,
      defaultColumn, // Be sure to pass the defaultColumn option
      filterTypes,
    },
    useFilters, // useFilters!
    useGlobalFilter, // useGlobalFilter!
    useSortBy
  )

  const handleAddFunc = (data) => {
    if (addFunc) {
      addFunc(data);
    }
    if (closeSearchModal) {
      closeSearchModal();
    }
  }

  return (
    <div className="table-loader-container">
      <div className={`table-container mb-3`}>
        <Table className="custom-table" striped>
          <thead>
            {headerGroups.map(headerGroup => (
              <tr {...headerGroup.getHeaderGroupProps()} >
                {headerGroup.headers.map(column => (
                  <th className="text-muted font-weight-normal" {...column.getHeaderProps(column.getSortByToggleProps())}>
                    <span className="ml-2">{column.render('Header')}</span>
                    <span>
                        {column.isSorted ? (column.isSortedDesc ? <i className="material-icons">arrow_drop_down</i> : <i className="material-icons">arrow_drop_up</i> ) : column.canFilter ? <i className="fas fa-sort ml-2"></i> : null}
                    </span>
                    <div className="mt-4">{column.canFilter ? column.render('Filter') : null}</div>
                  </th>
                ))}
              </tr>
            ))}
          </thead>
        <TableBody 
            getTableBodyProps={getTableBodyProps} 
            rows={rows} 
            prepareRow={prepareRow} 
            maySortableRows={maySortableRows}
            handleAddFunc={handleAddFunc}
            handleSortFunc={handleSortFunc} />
        </Table>
        { 
          !isLoadingData && rows.length === 0 && (  
            <div className="d-flex justify-content-center align-items-center mt-5">
              <p className="text-center">No se encontraron registros...</p>
            </div>
          )
        }   
      </div>
      {
        isLoadingData && (
          <div className="d-flex justify-content-center align-items-center table-loader">
            <Spinner animation="border" role="status">
              <span className="sr-only">Loading...</span>
            </Spinner>
          </div>
        )
      }
    </div>
  )
}

// Define a custom filter filter function!
function filterGreaterThan(rows, id, filterValue) {
  return rows.filter(row => {
    const rowValue = row.values[id]
    return rowValue >= filterValue
  })
}

// This is an autoRemove method on the filter function that
// when given the new filter value and returns true, the filter
// will be automatically removed. Normally this is just an undefined
// check, but here, we want to remove the filter if it's not a number
filterGreaterThan.autoRemove = val => typeof val !== 'number'

export default CustomTable;